import { useCallback, useEffect, useMemo, useState } from 'react'
import { CheckCircle2, ChevronLeft, ChevronRight, ExternalLink, RefreshCw, RotateCcw, Search, XCircle } from 'lucide-react'
import { SITE_CATALOG } from '../../shared/siteCatalog'
import { fetchReviewItems, updateSearchResult } from '../lib/api'

const REVIEW_PAGE_SIZE = 50
const REVIEW_FILTER_STORAGE_KEY = 'wiedersehen.review.filters.v1'

const STATUS_FILTERS = [
  { key: 'open', label: 'Offen' },
  { key: 'manual_review', label: 'Prüfen' },
  { key: 'maybe', label: 'Möglich' },
  { key: 'confirmed', label: 'Bestätigt' },
  { key: 'rejected', label: 'Abgelehnt' },
  { key: 'all', label: 'Alle' },
]

const SORT_OPTIONS = [
  { key: 'confidence_desc', label: 'Confidence absteigend' },
  { key: 'confidence_asc', label: 'Confidence aufsteigend' },
]

export default function ReviewView({ onOpenPerson, onChanged }) {
  const [filters, setFilters] = useState(readStoredFilters)
  const [data, setData] = useState({ counts: {}, platforms: [], results: [], pagination: defaultPagination() })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [acting, setActing] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const next = await fetchReviewItems(filters)
      if ((next.results?.length || 0) === 0 && next.pagination?.total > 0 && filters.offset > 0) {
        setFilters((current) => ({
          ...current,
          offset: Math.max(0, current.offset - current.limit),
        }))
        return
      }
      setData(next)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    window.sessionStorage.setItem(REVIEW_FILTER_STORAGE_KEY, JSON.stringify(filters))
  }, [filters])

  const platforms = useMemo(() => {
    const seen = new Set(data.platforms || [])
    for (const site of SITE_CATALOG) {
      if (site.strategy !== 'manual_link') seen.add(site.name)
    }
    return [...seen].sort((a, b) => a.localeCompare(b, 'de'))
  }, [data.platforms])

  const setFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value, offset: 0 }))
  }

  const setPageOffset = (offset) => {
    setFilters((current) => ({ ...current, offset: Math.max(0, offset) }))
  }

  const handleAction = async (result, action) => {
    setActing(`${result.id}:${action}`)
    setError('')
    try {
      await updateSearchResult(result.person.id, result.id, action)
      await load()
      onChanged?.()
    } catch (e) {
      setError(e.message)
    } finally {
      setActing('')
    }
  }

  const counts = data.counts || {}
  const openCount = (counts.maybe || 0) + (counts.manual_review || 0)
  const pagination = data.pagination || defaultPagination()
  const total = pagination.total || 0
  const pageStart = total === 0 ? 0 : pagination.offset + 1
  const pageEnd = Math.min(total, pagination.offset + (pagination.returned || data.results?.length || 0))

  return (
    <section className="review-view">
      <div className="review-tabs" role="tablist" aria-label="Review Status">
        {STATUS_FILTERS.map((item) => (
          <button
            key={item.key}
            className={`review-tab ${filters.status === item.key ? 'active' : ''}`}
            onClick={() => setFilter('status', item.key)}
            type="button"
          >
            <span>{item.label}</span>
            <span className="review-tab-count">{countFor(item.key, counts, openCount)}</span>
          </button>
        ))}
      </div>

      <div className="review-toolbar">
        <div className="review-search">
          <Search size={14} />
          <input
            className="form-input"
            value={filters.q}
            onChange={(e) => setFilter('q', e.target.value)}
            placeholder="Person, Username, Plattform..."
          />
        </div>
        <select
          className="form-select"
          value={filters.platform}
          onChange={(e) => setFilter('platform', e.target.value)}
        >
          <option value="">Alle Plattformen</option>
          {platforms.map((platform) => (
            <option key={platform} value={platform}>{platform}</option>
          ))}
        </select>
        <select
          className="form-select"
          value={filters.sort}
          onChange={(e) => setFilter('sort', e.target.value)}
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.key} value={option.key}>{option.label}</option>
          ))}
        </select>
        <button className="btn btn-ghost btn-sm" onClick={load} type="button" disabled={loading}>
          {loading ? <span className="spinner" /> : <RefreshCw size={13} />}
          Aktualisieren
        </button>
      </div>

      {error && (
        <div className="notice notice-error">
          <span>{error}</span>
          <button className="btn btn-ghost btn-sm" onClick={load}>Erneut laden</button>
        </div>
      )}

      {!loading && !error && (
        <div className="review-summary">
          <span>
            {total === 0
              ? 'Keine Kandidaten im aktuellen Filter'
              : `${pageStart}-${pageEnd} von ${total} Kandidaten`}
          </span>
          {(filters.q || filters.platform || filters.status !== 'open') && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setFilters(defaultFilters())}
              type="button"
            >
              Filter zurücksetzen
            </button>
          )}
        </div>
      )}

      {loading && (
        <div className="loading-card">
          <span className="spinner" />
          Lade Kandidaten...
        </div>
      )}

      {!loading && data.results?.length === 0 && (
        <div className="empty review-empty">
          <h3>Keine Kandidaten</h3>
          <p>Der aktuelle Filter enthält keine verifizierbaren Kandidaten.</p>
        </div>
      )}

      {!loading && data.results?.length > 0 && (
        <div className="review-list">
          {data.results.map((result) => (
            <ReviewRow
              key={result.id}
              result={result}
              acting={acting}
              onAction={handleAction}
              onOpenPerson={onOpenPerson}
            />
          ))}
        </div>
      )}

      {!loading && total > 0 && (
        <ReviewPager
          pagination={pagination}
          onPage={setPageOffset}
          disabled={loading}
        />
      )}
    </section>
  )
}

function ReviewRow({ result, acting, onAction, onOpenPerson }) {
  const isRejected = result.result_status === 'rejected'
  const isConfirmed = result.result_status === 'likely' && result.confidence >= 80
  const canReview = !isRejected && !isConfirmed
  const personName = [result.person.first_name, result.person.last_name].filter(Boolean).join(' ')

  return (
    <article className={`review-row review-row-${result.result_status}`}>
      <div className="review-person">
        <button className="link-button review-person-name" onClick={() => onOpenPerson?.(result.person.id)} type="button">
          {personName || `Person #${result.person.id}`}
        </button>
        <div className="review-person-meta">
          {result.person.nickname && <span>"{result.person.nickname}"</span>}
          {result.person.city && <span>{result.person.city}</span>}
          <span className={`badge badge-${result.person.status}`}>{result.person.status}</span>
        </div>
      </div>

      <div className="review-result-main">
        <div className="review-result-title">
          <span className="result-platform">{result.platform}</span>
          {result.username && <span className="review-username">@{result.username}</span>}
        </div>
        <a className="result-link review-url" href={result.profile_url} target="_blank" rel="noopener">
          {result.profile_url} <ExternalLink size={10} style={{ verticalAlign: 'middle' }} />
        </a>
        <div className="result-meta">
          {result.priority && <span className="priority-pill">P{result.priority.score} · {result.priority.reason}</span>}
          <span>{result.confidence ?? 0}%</span>
          <span>{statusLabel(result.result_status)}</span>
          {result.evidence && <span>{evidenceLabel(result.evidence)}</span>}
        </div>
      </div>

      <div className="review-actions">
        {canReview && (
          <>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => onAction(result, 'confirm')}
              disabled={acting === `${result.id}:confirm`}
              type="button"
            >
              {acting === `${result.id}:confirm` ? <span className="spinner" /> : <CheckCircle2 size={12} />}
              Bestätigen
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={() => onAction(result, 'reject')}
              disabled={acting === `${result.id}:reject`}
              type="button"
            >
              {acting === `${result.id}:reject` ? <span className="spinner" /> : <XCircle size={12} />}
              Ablehnen
            </button>
          </>
        )}
        {isRejected && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => onAction(result, 'reopen')}
            disabled={acting === `${result.id}:reopen`}
            type="button"
          >
            {acting === `${result.id}:reopen` ? <span className="spinner" /> : <RotateCcw size={12} />}
            Wieder öffnen
          </button>
        )}
      </div>
    </article>
  )
}

function countFor(key, counts, openCount) {
  if (key === 'open') return openCount
  if (key === 'confirmed') return counts.confirmed || 0
  if (key === 'all') return counts.total || 0
  return counts[key] || 0
}

function ReviewPager({ pagination, onPage, disabled }) {
  const page = Math.floor((pagination.offset || 0) / (pagination.limit || REVIEW_PAGE_SIZE)) + 1
  const pageCount = Math.max(1, Math.ceil((pagination.total || 0) / (pagination.limit || REVIEW_PAGE_SIZE)))

  return (
    <div className="review-pager">
      <button
        className="btn btn-ghost btn-sm"
        onClick={() => onPage(pagination.previous_offset)}
        disabled={disabled || pagination.previous_offset === null}
        type="button"
      >
        <ChevronLeft size={13} />
        Zurück
      </button>
      <span className="review-page-indicator">Seite {page} von {pageCount}</span>
      <button
        className="btn btn-ghost btn-sm"
        onClick={() => onPage(pagination.next_offset)}
        disabled={disabled || !pagination.has_more}
        type="button"
      >
        Weiter
        <ChevronRight size={13} />
      </button>
    </div>
  )
}

function defaultFilters() {
  return {
    status: 'open',
    platform: '',
    q: '',
    sort: 'confidence_desc',
    limit: REVIEW_PAGE_SIZE,
    offset: 0,
  }
}

function readStoredFilters() {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(REVIEW_FILTER_STORAGE_KEY) || '{}')
    const next = { ...defaultFilters(), ...stored }
    if (!STATUS_FILTERS.some((item) => item.key === next.status)) next.status = 'open'
    if (!SORT_OPTIONS.some((item) => item.key === next.sort)) next.sort = 'confidence_desc'
    next.limit = REVIEW_PAGE_SIZE
    next.offset = Number.isInteger(Number(next.offset)) && Number(next.offset) >= 0 ? Number(next.offset) : 0
    next.platform = typeof next.platform === 'string' ? next.platform : ''
    next.q = typeof next.q === 'string' ? next.q : ''
    return next
  } catch {
    return defaultFilters()
  }
}

function defaultPagination() {
  return {
    limit: REVIEW_PAGE_SIZE,
    offset: 0,
    returned: 0,
    total: 0,
    has_more: false,
    next_offset: null,
    previous_offset: null,
  }
}

function statusLabel(status) {
  const labels = {
    likely: 'wahrscheinlich',
    maybe: 'möglich',
    manual_review: 'prüfen',
    not_found: 'nicht gefunden',
    error: 'Fehler',
    rejected: 'abgelehnt',
  }
  return labels[status] || status
}

function evidenceLabel(evidence) {
  const labels = {
    manual_review_required: 'manuell angelegt',
    api_signal_not_reliable_enough: 'API-Signal unsicher',
    json_missing_match: 'API meldet keinen Treffer',
    manual_confirmed: 'manuell bestätigt',
    manual_rejected: 'manuell abgelehnt',
    manual_reopened: 'wieder geöffnet',
  }

  if (labels[evidence]) return labels[evidence]
  if (evidence.startsWith('json_match:')) return 'API-Match'
  if (evidence.startsWith('missing_status:')) return '404/fehlend'
  if (evidence.startsWith('ambiguous_status:')) return 'uneindeutiger Status'
  return evidence
}
